import React, { useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import GlobalPopUp from '../../../../../../components/utility/globalPopUp';
import { thousandSeparator } from '../../../../../../utils/constants';
import styles from "../../../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import CatalogOrderProductRow from './catalogOrderProductRow';

interface CatalogProductOptionalData {
    size?: string;
    color?: string;
    [key: string]: string | undefined;
}

interface EcomProduct {
    ecom_product_name?: string;
    ecom_product_image?: string;
}

interface ProductItem {
    product_retailer_id?: string;
    quantity?: number;
    item_price?: number;
    currency?: string;
    ecom_product?: EcomProduct;
    catalog_product_optional_data?: CatalogProductOptionalData;
}

const formatAttributes = (optionalData?: CatalogProductOptionalData) => {
    if (!optionalData) return '';

    const parts: string[] = [];
    if (optionalData?.size) {
        parts.push(`Size: ${optionalData?.size}`);
    }
    if (optionalData?.color) {
        const colorLabel = optionalData?.color?.charAt(0)?.toUpperCase() + optionalData?.color?.slice(1);
        parts.push(colorLabel);
    }
    return parts.join(' · ');
};

const toNumber = (value: unknown) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const getItemUnitPrice = (item: ProductItem) => toNumber(item?.item_price);

const getItemQuantity = (item: ProductItem) => toNumber(item?.quantity);

const getItemCurrency = (item: ProductItem) => item?.currency ?? '';

const getItemTotal = (item: ProductItem) => getItemQuantity(item) * getItemUnitPrice(item);

const CatalogOrderReceivedMessage = (props: { productItems?: ProductItem[] }) => {
    const { productItems = [] } = props;
    const [openPopup, setOpenPopup] = useState(false);

    const totalItemCount = useMemo(() => {
        return productItems?.reduce((total, item) => total + getItemQuantity(item), 0);
    }, [productItems]);

    const totalAmount = useMemo(() => {
        return productItems?.reduce((total, item) => total + getItemTotal(item), 0);
    }, [productItems]);

    const currency = productItems?.[0]?.currency ?? '';

    const visibleProducts = productItems?.length <= 1 ? productItems : productItems?.slice(0, 1);
    const productCountLabel = `${productItems?.length} Product${productItems?.length !== 1 ? 's' : ''}`;

    return (
        <>
            <Grid item className={styles.catalogOrderMainGrid}>
                <Grid container className={styles.catalogOrderHeader} flexWrap="nowrap" alignItems="center">
                    <Grid item className={styles.catalogOrderCartIconGrid}>
                        <ShoppingCartIcon className={styles.catalogOrderCartIcon} />
                    </Grid>
                    <Grid item className={styles.catalogOrderHeaderContent}>
                        <Typography className={styles.catalogOrderTitle}>Catalog order received</Typography>
                        <Typography className={styles.catalogOrderItemCount}>{productCountLabel}</Typography>
                    </Grid>
                    <Grid item className={styles.catalogOrderTotalAmountGrid}>
                        <Typography className={styles.catalogOrderTotalAmount}>
                            {`${currency} ${thousandSeparator(totalAmount)}`}
                        </Typography>
                    </Grid>
                </Grid>

                {visibleProducts.length > 0 && (
                    <Grid item className={styles.catalogOrderProductsSection}>
                        {visibleProducts.map((item, index) => (
                            <React.Fragment key={item?.product_retailer_id ?? index}>
                                {index > 0 && <div className={styles.catalogOrderProductDivider} />}
                                <CatalogOrderProductRow 
                                    productName={item?.ecom_product?.ecom_product_name ?? ''}
                                    productImage={item?.ecom_product?.ecom_product_image ?? ''}
                                    attributes={formatAttributes(item?.catalog_product_optional_data)}
                                    quantity={getItemQuantity(item)}
                                    currency={getItemCurrency(item)}
                                    unitPrice={getItemUnitPrice(item)}
                                    lineTotal={getItemTotal(item)}
                                />
                            </React.Fragment>
                        ))}
                    </Grid>
                )}

                {productItems.length > 1 && (
                    <Grid item className={styles.catalogOrderViewAllGrid}>
                        <Typography
                            className={styles.catalogOrderViewAllBtn}
                            onClick={() => setOpenPopup(true)}
                        >
                            View all items
                        </Typography>
                    </Grid>
                )}
            </Grid>

            <GlobalPopUp
                open={openPopup}
                handleClose={() => setOpenPopup(false)}
                onClose={() => setOpenPopup(false)}
                flagShowCloseIcon
                title={productCountLabel}
                titleAlignment="left"
                titlePadding="20px 16px 19px"
                borderRadius="8px"
                paperMinWidth="330px"
                paperMaxwidth="380px"
                overflow="hidden"
                className={styles.catalogOrderPopupDialog}
            >
                <div className={styles.catalogOrderPopupHeaderDivider} />
                <Grid item className={styles.catalogOrderPopupContent}>
                    {productItems.map((item, index) => (
                        <React.Fragment key={item?.product_retailer_id ?? index}>
                            {index > 0 && <div className={styles.catalogOrderPopupProductDivider} />}
                            <CatalogOrderProductRow
                                productName={item?.ecom_product?.ecom_product_name ?? ''}
                                productImage={item?.ecom_product?.ecom_product_image ?? ''}
                                attributes={formatAttributes(item?.catalog_product_optional_data)}
                                quantity={getItemQuantity(item)}
                                currency={getItemCurrency(item)}
                                unitPrice={getItemUnitPrice(item)}
                                lineTotal={getItemTotal(item)}
                                showQtyPriceBreakdown 
                            />
                        </React.Fragment>
                    ))}
                </Grid>

                <Grid container className={styles.catalogOrderPopupFooter} justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Typography className={styles.catalogOrderPopupFooterLabel}>{`${totalItemCount} items total`}</Typography>
                    </Grid>
                    <Grid item>
                        <Typography className={styles.catalogOrderPopupFooterTotal}>{`${currency} ${thousandSeparator(totalAmount)}`}</Typography>
                    </Grid>
                </Grid>
            </GlobalPopUp>
        </>
    );
};

export default CatalogOrderReceivedMessage;
